import { useCallback, useEffect } from "react";
import { PriceList } from "@forgepoint/shared";
import { EntitySelector } from "@/components/EntitySelector";
import { usePriceListsStore } from "@/modules/priceLists/priceListsStore";

interface PriceListSelectorProps {
  currentlySelectedId: string | null;
  onSelectId: (priceListId: string | null) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  size?: "default" | "sm";
}

/** Searchable selector for choosing a price list. */
export function PriceListSelector({
  currentlySelectedId,
  onSelectId,
  placeholder = "Select a price list...",
  className,
  disabled = false,
  size = "default",
}: PriceListSelectorProps) {
  const { priceLists, listLoading, fetchPriceLists, fetchPriceList } =
    usePriceListsStore();

  useEffect(() => {
    if (currentlySelectedId && !priceLists.has(currentlySelectedId)) {
      fetchPriceList(currentlySelectedId);
    }
  }, [currentlySelectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchEntities = useCallback(
    async (search?: string) => fetchPriceLists({ search }),
    [fetchPriceLists],
  );

  const getDisplayName = useCallback(
    (priceList: PriceList) => priceList.name,
    [],
  );

  const getSecondaryInfo = useCallback((priceList: PriceList) => {
    return priceList.description ?? "";
  }, []);

  return (
    <EntitySelector<PriceList>
      entities={priceLists}
      listLoading={listLoading}
      fetchEntities={fetchEntities}
      getDisplayName={getDisplayName}
      getSecondaryInfo={getSecondaryInfo}
      currentlySelectedId={currentlySelectedId}
      onSelectId={onSelectId}
      placeholder={placeholder}
      searchPlaceholder="Search price lists..."
      className={className}
      disabled={disabled}
      size={size}
      entityLabel="price lists"
    />
  );
}
